import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import Header from "@/components/Header/Header";
import Footer from "@/components/Footer/Footer";
import Landing from "@/pages/Landing";
import Lobby from "@/pages/Lobby";
import Events from "@/pages/Events";
import HostEvent from "@/pages/HostEvent/HostEvent";
import Sponsor from "@/pages/Sponsor/Sponsor";
import SponsorThanks from "@/pages/SponsorThanks/SponsorThanks";
import SponsorDashboard from "@/pages/SponsorDashboard/SponsorDashboard";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ApplyModerator from "@/pages/ApplyModerator/ApplyModerator";
import EventDetail from "@/pages/EventDetail";
import Network from "@/pages/Network/Network";
import BrandDetail from "@/pages/BrandDetail";
import SponsorEdit from "@/pages/SponsorEdit/SponsorEdit";
import HostDashboard from "@/pages/HostDashboard/HostDashboard";
import Admin from "@/pages/Admin/Admin";
import SponsorRegistry from "@/pages/SponsorRegistry/SponsorRegistry";
import SponsorPreferences from "@/pages/SponsorPreferences";
import SponsorshipCalendar from "@/pages/SponsorshipCalendar";
import MyEvents from "@/pages/MyEvents/MyEvents";
import RetrieveRegistration from "@/pages/RetrieveRegistration/RetrieveRegistration";
// import Messages from "@/pages/Messages";
import AdminOnboarding from "@/pages/AdminOnboarding/AdminOnboarding";
import MessagesPage from "@/pages/MessagesPage/MessagesPage";
import ScrollToTop from "./components/helper/ScrollToTop";
import TableDetails from "@/pages/TableDetails";
import JoinMeetingLayout from "@/pages/JoinMeetingLayout";
import MeetingRoom from "@/pages/MeetingRoom/MeetingRoom";
import GuestJoinSessionPage from "./pages/GuestJoinSessionPage";
import CreateUserMeetingPage from "./pages/UserMeet/CreateUserMeetingPage";
import UserMeetPage from "./pages/UserMeet/UserMeetPage";
import JoinUserMeetingPage from "./pages/UserMeet/JoinUserMeetingPage";
import UserMeetRoomPage from "./pages/UserMeet/UserMeetRoomPage";
import MeetingInvitePage from "./pages/UserMeet/MeetingInvitePage";
import MeetRedirect from "./pages/UserMeet/MeetRedirect";
import NotFoundPage from "./NotFoundPage";
// import UpcomingMeetingLobby from "./pages/UserMeet/UpcomingMeetingLobby";

function AppLayout() {
  const location = useLocation();
  const isMeetingRoom = location.pathname.startsWith('/meeting-room');
  const isUserMeetingRoom = location.pathname.startsWith('/user-meet/room');

  return (
    <div data-testid="app-shell" className="min-h-screen flex flex-col">
      {!isMeetingRoom && !isUserMeetingRoom && <Header />}
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/lobby" element={<Lobby />} />
          <Route path="/events" element={<Events />} />
          <Route path="/host" element={<HostEvent />} />
          <Route path="/sponsor" element={<Sponsor />} />
          <Route path="/sponsor/thanks" element={<SponsorThanks />} />
          <Route path="/sponsor-dashboard" element={<SponsorDashboard />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          {/* /membership merged into /network — redirect for backward-compat */}
          <Route path="/membership" element={<Navigate to="/network#apply-membership" replace />} />
          <Route path="/moderators/apply" element={<ApplyModerator />} />
          <Route path="/events/:eventId" element={<EventDetail />} />
          <Route path="/network" element={<Network />} />
          <Route path="/brands/:company" element={<BrandDetail />} />
          <Route path="/sponsors/edit" element={<SponsorEdit />} />
          <Route path="/host/dashboard" element={<HostDashboard />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/sponsor-registry" element={<SponsorRegistry />} />
          <Route path="/sponsor-registry/preferences" element={<SponsorPreferences />} />
          <Route path="/sponsorship-calendar" element={<SponsorshipCalendar />} />
          <Route path="/my-events" element={<MyEvents />} />
          <Route path="/retrieve" element={<RetrieveRegistration />} />
          <Route path="/messages" element={<MessagesPage />} />
          <Route path="/messages/:userId" element={<MessagesPage />} />
          <Route path="/admin/onboarding" element={<AdminOnboarding />} />
          <Route path="/events/:eventId/sessions/:sessionId/tables/:tableId" element={<TableDetails />}/>
          <Route path="/events/:eventId/sessions/:sessionId/tables/:tableId/join" element={<JoinMeetingLayout />} />
          <Route path="/meeting-room" element={<MeetingRoom />} />
          <Route path="/events/session/:sessionId/join" element={<GuestJoinSessionPage />} />

          {/* User-Meet */}
          <Route path="/meet/:joinCode" element={<MeetRedirect />} />

          {/* ✅ Static named segments first */}
          <Route path="/user-meet" element={<UserMeetPage />} />
          <Route path="/user-meet/create" element={<CreateUserMeetingPage />} />
          <Route path="/user-meet/join/:joinCode" element={<JoinUserMeetingPage />} />
          <Route path="/user-meet/room/:joinCode" element={<UserMeetRoomPage />} />
          {/* <Route path="/user-meet/upcoming/:joinCode" element={<UpcomingMeetingLobby />} /> */}
          <Route path="/user-meet/:joinCode/invite" element={<MeetingInvitePage />} />


          {/* ─── 404 Catch-All ─── */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
      {!isMeetingRoom && <Footer />}
      <Toaster
        position="bottom-right"
        theme="dark"
        toastOptions={{
          style: {
            background: "rgba(10,29,26,0.95)",
            border: "1px solid rgba(198,168,124,0.3)",
            color: "#F0F4F2",
          },
        }}
      />
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <AppLayout />
      </BrowserRouter>
    </AuthProvider>
  );
}
